import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { BILLING_DETAILS_PATH, REDIS_BUSINESS_GET_PATH } from "../../constants/apipaths";

export type RestrictionReason =
  | "unknown"
  | "non_paying"
  | "overdue"
  | "suspended"
  | "inactive_contract";

export interface BusinessAccessState {
  businessId?: string;
  isBillingLoading: boolean;
  billingLastUpdatedAt?: number;
  isRestricted: boolean;
  isNonPaying: boolean;
  reason?: RestrictionReason;
}

const initialState: BusinessAccessState = {
  businessId: undefined,
  isBillingLoading: false,
  billingLastUpdatedAt: undefined,
  isRestricted: false,
  isNonPaying: false,
  reason: "unknown"
};

function determineNonPaying(data: any): {
  isNonPaying: boolean;
  isRestricted: boolean;
  reason?: RestrictionReason;
} {
  const statusRaw =
    data?.status ??
    data?.Status ??
    data?.billingStatus ??
    data?.BillingStatus ??
    data?.ContractStatus ??
    data?.contractStatus ??
    data;

  // contractStatus-specific handling (PaymentDetails API returns ContractStatus)
  const contract = String(data?.ContractStatus ?? data?.contractStatus ?? "")
    .toLowerCase()
    .trim();
  if (contract) {
    if (contract === "active") {
      return { isNonPaying: false, isRestricted: false, reason: undefined };
    }
    if (contract.includes("suspend")) {
      return { isNonPaying: true, isRestricted: true, reason: "suspended" };
    }
    if (contract.includes("overdue") || contract.includes("arrear")) {
      return { isNonPaying: true, isRestricted: true, reason: "overdue" };
    }
    return {
      isNonPaying: true,
      isRestricted: true,
      reason: "inactive_contract"
    };
  }

  if (typeof statusRaw === "boolean") {
    const nonPaying = statusRaw === false;
    return {
      isNonPaying: nonPaying,
      isRestricted: nonPaying,
      reason: nonPaying ? "non_paying" : undefined
    };
  }
  if (typeof statusRaw === "number") {
    const nonPaying = statusRaw === 0;
    return {
      isNonPaying: nonPaying,
      isRestricted: nonPaying,
      reason: nonPaying ? "non_paying" : undefined
    };
  }

  const value = String(statusRaw ?? "").toLowerCase();
  if (!value && typeof data === "string") {
    const raw = data.toLowerCase();
    const nonPaying = raw.includes("non") || raw.includes("arrear");
    return {
      isNonPaying: nonPaying,
      isRestricted: nonPaying,
      reason: nonPaying ? "non_paying" : undefined
    };
  }

  if (["false", "0", "nonpaying", "non-paying"].includes(value)) {
    return { isNonPaying: true, isRestricted: true, reason: "non_paying" };
  }

  if (
    value.includes("overdue") ||
    value.includes("arrear") ||
    value.includes("suspended")
  ) {
    const reason: RestrictionReason = value.includes("suspend")
      ? "suspended"
      : value.includes("overdue") || value.includes("arrear")
        ? "overdue"
        : "non_paying";
    return { isNonPaying: true, isRestricted: true, reason };
  }

  return { isNonPaying: false, isRestricted: false, reason: undefined };
}

export const fetchBillingRestriction = createAsyncThunk(
  "businessAccess/fetchBillingRestriction",
  async (businessId: string) => {
    const token = localStorage?.getItem("businessAccessToken");
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    // Step 1: Check global feature toggle via Redis
    const redisUrl = `${baseUrl}${REDIS_BUSINESS_GET_PATH}`;
    const redisRes = await axios.post(
      redisUrl,
      { key: "EnableNonPayingBusinessRestrictedAccessFeature" },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    const redisData = redisRes?.data;
    console.log("🚀 ~ redisData:", redisData)
    const rawValue = redisData?.value;
    console.log("🚀 ~ rawValue:", rawValue)

    // Feature OFF (null, false, "false", 0, "0") → skip billing check, allow full access
    const featureEnabled =
      rawValue != null &&
      rawValue !== false &&
      rawValue !== 0 &&
      String(rawValue).toLowerCase().trim() !== "false" &&
      String(rawValue).toLowerCase().trim() !== "0";

    if (!featureEnabled) {
      return {
        isNonPaying: false,
        isRestricted: false,
        reason: undefined
      };
    }

    // Step 2: Feature is ON, check billing status and restrict if needed
    const url = `${baseUrl}${BILLING_DETAILS_PATH}/${businessId}`;

    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    const data = res?.data;

    // If API returns 204 No Content or an effectively empty payload,
    // treat it as restricted / non‑paying so we block actions safely.
    if (
      data == null ||
      (typeof data === "string" && data.trim() === "") ||
      (typeof data === "object" && Object.keys(data).length === 0)
    ) {
      return {
        isNonPaying: true,
        isRestricted: true,
        reason: "inactive_contract" as RestrictionReason
      };
    }

    return determineNonPaying(data);
  }
);

const businessAccessSlice = createSlice({
  name: "businessAccess",
  initialState,
  reducers: {
    setBusinessId(state, action: PayloadAction<string | undefined>) {
      if (state.businessId !== action.payload) {
        state.businessId = action.payload;
        // reset on business change until fetched
        state.isRestricted = false;
        state.isNonPaying = false;
        state.reason = "unknown";
        state.billingLastUpdatedAt = undefined;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingRestriction.pending, (state) => {
        state.isBillingLoading = true;
      })
      .addCase(fetchBillingRestriction.fulfilled, (state, action) => {
        state.isBillingLoading = false;
        state.isNonPaying = action.payload.isNonPaying;
        state.isRestricted = action.payload.isRestricted;
        state.reason = action.payload.reason;
        state.billingLastUpdatedAt = Date.now();
      })
      .addCase(fetchBillingRestriction.rejected, (state) => {
        // Don't block user on API errors: default to not restricted
        state.isBillingLoading = false;
        state.isNonPaying = false;
        state.isRestricted = false;
        state.reason = undefined;
        state.billingLastUpdatedAt = Date.now();
      });
  }
});

export const { setBusinessId } = businessAccessSlice.actions;
export default businessAccessSlice.reducer;


